import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {CustomFieldType} from '@ee/common/enums';
import {CaseSettings} from '@ee/common/models';
import {UploaderComponent} from '@ee/common/uploader';

@Component({
  selector: 'ee-court-judgement',
  template: `
    @if (!!form) {
      <div [formGroup]="form" class="flex flex-col justify-start items-stretch">
        <div class="in-row-grid wider" [ngClass]="{'mb-2': !forceVertical}">
          @if (caseSettings?.judgement_options?.length) {
            <mat-form-field class="compact white" [ngClass]="{'w-full mb-4': forceVertical}" appearance="outline">
              <mat-label>Judgment</mat-label>
              <mat-select formControlName="judgement">
                @for (option of caseSettings.judgement_options; track option) {
                  <mat-option [value]="option">
                    {{ option }}
                  </mat-option>
                }
              </mat-select>
              <mat-hint>{{form?.get('judgement').hasValidator(Validators.required) ? 'Required' : 'Optional'}}</mat-hint>
            </mat-form-field>
          }
          <mat-form-field class="compact white" [ngClass]="{'w-full mb-4': forceVertical}" appearance="outline">
            <mat-label>Judgment Amount</mat-label>
            <span matPrefix>$ &nbsp;</span>
            <input matInput type="number" formControlName="judgement_amount">
            <mat-hint>{{form?.get('judgement_amount').hasValidator(Validators.required) ? 'Required' : 'Optional'}}</mat-hint>
          </mat-form-field>
        </div>
        <div class="in-row-grid wider" [ngClass]="{'mb-2': !forceVertical}">
          <mat-form-field class="compact white" [ngClass]="{'w-full mb-4': forceVertical}" appearance="outline">
            <mat-label>Judgment Date</mat-label>
            <input matInput [matDatepicker]="picker" formControlName="judgement_date">
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            <mat-hint>{{form?.get('judgement_date').hasValidator(Validators.required) ? 'Required' : 'Optional'}}</mat-hint>
          </mat-form-field>
          @if (caseSettings?.court_result_options?.length) {
            <mat-form-field class="compact white" appearance="outline"
              [ngClass]="{'w-full mb-4': forceVertical}">
              <mat-label>Docket Result</mat-label>
              <mat-select formControlName="court_result">
                @for (option of caseSettings.court_result_options; track option) {
                  <mat-option [value]="option">
                    {{ option }}
                  </mat-option>
                }
              </mat-select>
              <mat-hint>{{form?.get('court_result').hasValidator(Validators.required) ? 'Required' : 'Optional'}}</mat-hint>
            </mat-form-field>
          }
        </div>
        @if (!hideComment) {
          <div>
            <mat-form-field class="compact white" [ngClass]="{'w-full': forceVertical}" appearance="outline">
              <mat-label>Comment</mat-label>
              <textarea matInput rows="4" formControlName="comment"></textarea>
              @if (form?.get('comment').hasError('required')) {
                <mat-error>
                  Please provide a reason for the change.
                </mat-error>
              }
              <mat-hint>Optional</mat-hint>
            </mat-form-field>
          </div>
        }
        @if (showFileUpload) {
          <div class="mt-2">
            <ee-uploader
              label="Judgment Document"
              hint="Optional. PDF, JPEG, PNG, or TIFF."
              [form]="fileControl"
              accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
              (uploadReady)="onFileReady($event)"
              (uploadCleared)="onFileCleared()">
            </ee-uploader>
          </div>
        }
      </div>
    }
    `,
  styles: [`
    .mat-mdc-text-field-wrapper {
      background-color: var(--color-surface, #fff);
    }

    :host-context(.dark-theme) {
      .mat-mdc-text-field-wrapper {
        background-color: #3d3d3d !important;
      }

      .mat-mdc-form-field {
        .mdc-floating-label {
          color: rgba(255, 255, 255, 0.7) !important;
        }

        input, textarea {
          color: rgba(255, 255, 255, 0.87) !important;
        }

        .mat-mdc-form-field-hint {
          color: rgba(255, 255, 255, 0.5) !important;
        }

        .mat-mdc-select-value {
          color: rgba(255, 255, 255, 0.87) !important;
        }

        .mat-mdc-select-arrow {
          color: rgba(255, 255, 255, 0.7) !important;
        }
      }

      // Prefix styling
      [matPrefix] {
        color: rgba(255, 255, 255, 0.7) !important;
      }
    }
  `],
  standalone: false
})
export class CourtJudgementComponent implements OnChanges, OnDestroy {
  @Input() caseSettings: CaseSettings | null;
  @Input({required: true}) form: UntypedFormGroup;
  @Input() forceVertical = false;
  @Input() hideComment = false;
  @Input() showFileUpload = false;
  /**
   * The "Schedule a New Court Date" path (a continuance). The first Judgment option is not selected, and an
   * untouched default is cleared, so continuing a hearing does not record Judgment and Possession.
   */
  @Input() scheduleNewCourtDate = false;
  @Output() uploadReady = new EventEmitter<{ upload_id: string; original_format: string }>();
  @Output() uploadCleared = new EventEmitter<void>();
  public name: string;

  fileControl = new UntypedFormControl(null);

  @ViewChild(UploaderComponent) private uploader?: UploaderComponent;

  subs: Subscription[] = [];
  /** True when this component, not the user and not a saved task, put the first option in the control. */
  private judgementDefaulted = false;
  private appliedJudgementDefault: unknown = null;

  constructor() {
  }

  onFileReady(event: { upload_id: string; original_format: string }): void {
    this.uploadReady.emit(event);
  }

  onFileCleared(): void {
    this.uploadCleared.emit();
  }

  /**
   * Clears any staged judgment document: fully resets the embedded uploader (aborting an in-flight
   * upload, deleting the staged temp object, and clearing the file tile) and resets the bound control.
   * The uploader's own reset emits `uploadCleared`, which propagates to the host so its tracked
   * upload id is cleared in lock-step — leaving no "attached but untracked" phantom state.
   */
  public clearUpload(): void {
    if (this.uploader) {
      this.uploader.clearSelectedFile(this.fileControl);
    } else {
      // Uploader not rendered (e.g. showFileUpload false): still clear the bound control and notify.
      this.fileControl.reset(null);
      this.uploadCleared.emit();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // When the host swaps in a fresh form instance (e.g. a bulk reset via
    // GenerateDailyDocketBulkEditForm), clear any staged file so the uploader UI can't keep
    // showing a document the host has already untracked.
    if (changes['form'] && !changes['form'].firstChange) {
      this.clearUpload();
      this.judgementDefaulted = false;
    }
    this.applyJudgementDefault();
  }

  /**
   * The firm's first Judgment option, unless this is a continuance. Only an empty control is filled, so a later
   * input change (the upload toggle) cannot replace a selection. A continued hearing leaves it unset, and clears
   * a default this component just applied, so Judgment and Possession is not recorded for a date the user moved.
   */
  private applyJudgementDefault(): void {
    const judgement = this.form?.get('judgement');
    if (!judgement) {
      return;
    }
    if (this.scheduleNewCourtDate) {
      // Only the value this component applied is cleared: a host that replaced it (even without marking the
      // control dirty) has made its own choice.
      if (this.judgementDefaulted && !judgement.dirty && judgement.value === this.appliedJudgementDefault) {
        judgement.setValue(null);
      }
      this.judgementDefaulted = false;
      return;
    }
    const first = this.caseSettings?.judgement_options?.[0];
    if (first && !judgement.value) {
      judgement.setValue(first);
      this.judgementDefaulted = true;
      this.appliedJudgementDefault = first;
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  protected readonly CustomFieldType = CustomFieldType;
  protected readonly Validators = Validators;
}
